import * as React from 'react';

import { useQuery } from '@apollo/client/react';
import isEmpty from 'lodash/isEmpty';

import { get } from 'js/lib/user';

import type {
  OnDemandLearnerMaterialsQuery,
  OnDemandLearnerMaterialsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/components/modals/__generated__/onDemandLearnerMaterialsQuery';
import { COURSE_PROGRESS_THRESHOLD } from 'bundles/enterprise-legacy-learner-home/constants/forcedSSORecoveryEmail';
import onDemandLearnerMaterialsQuery from 'bundles/enterprise-legacy-learner-home/queries/onDemandLearnerMaterialsQuery';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import LatestEnrolledProductsForOrgQuery from 'bundles/unified-home-common/queries/LatestEnrolledProductsForOrgQuery';
import type {
  LatestEnrolledProductsForOrgQuery as LatestEnrolledProductsForOrgQueryType,
  LatestEnrolledProductsForOrgQueryVariables as LatestEnrolledProductsForOrgQueryVariablesType,
} from 'bundles/unified-home-common/queries/__generated__/LatestEnrolledProductsForOrgQuery';

// Limit for checking enrolled courses across all org programs
const ORG_ENROLLED_PRODUCTS_LIMIT = 10;

/**
 * Hook to check if user has significant course progress (>5%) across ALL programs in the current org.
 *
 * This is a truly org-scoped implementation that uses `latestEnrolledCourseActivitiesByThirdPartyOrganizationAndUser`
 * to fetch enrolled courses across all programs in the organization, not just a single program.
 *
 * Key differences from useOrgScopedSignificantCourseProgress:
 * - Queries by thirdPartyOrganizationId instead of programId
 * - Checks course progress across ALL programs in the org (up to 50 courses)
 * - Does not depend on useProgramMembershipsAndInvitations context
 *
 * @returns {Object} - Contains hasSignificantCourseProgress boolean and isLoading state
 */
const useOrgScopedSignificantCourseProgress = ({ skip = false }: { skip?: boolean } = {}) => {
  const thirdPartyOrg = useThirdPartyOrganization();
  const userId = get().id;

  // Query enrolled courses across all programs in the org
  const { data: productsData, loading: isProductsLoading } = useQuery<
    LatestEnrolledProductsForOrgQueryType,
    LatestEnrolledProductsForOrgQueryVariablesType
  >(LatestEnrolledProductsForOrgQuery, {
    variables: {
      thirdPartyOrganizationId: thirdPartyOrg?.id || '',
      userId: userId?.toString() || '',
      limit: ORG_ENROLLED_PRODUCTS_LIMIT,
    },
    skip: skip || !thirdPartyOrg?.id || !userId,
    ssr: false,
  });

  // Extract course IDs from products (filter for COURSE type only)
  const courseIds = React.useMemo(() => {
    const elements =
      productsData?.ProgramCurriculumProductsV1Resource?.latestEnrolledCourseActivitiesByThirdPartyOrganizationAndUser
        ?.elements ?? [];
    // productItemId is the courseId for course products
    return elements.map((product) => product?.productItemId).filter(Boolean);
  }, [productsData]);

  // Query progress data for all enrolled courses
  const { data: learnerMaterialsData, loading: isLearnerMaterialsLoading } = useQuery<
    OnDemandLearnerMaterialsQuery,
    OnDemandLearnerMaterialsQueryVariables
  >(onDemandLearnerMaterialsQuery, {
    skip: skip || isEmpty(courseIds),
    variables: {
      courseIds,
    },
  });

  const learnerMaterialsArray = learnerMaterialsData?.OnDemandLearnerMaterialsV1Resource?.courses?.elements;

  // Check if any course has progress > 5% (COURSE_PROGRESS_THRESHOLD)
  const hasSignificantCourseProgress = React.useMemo(() => {
    return (
      !!learnerMaterialsArray &&
      !isEmpty(learnerMaterialsArray) &&
      learnerMaterialsArray.some((item) =>
        item?.summaryCoreProgressPercentage ? item.summaryCoreProgressPercentage > COURSE_PROGRESS_THRESHOLD : false
      )
    );
  }, [learnerMaterialsArray]);

  const isLoading = isProductsLoading || isLearnerMaterialsLoading;

  return {
    hasSignificantCourseProgress,
    isLoading,
  };
};

export default useOrgScopedSignificantCourseProgress;
